import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Info } from 'lucide-react';

import { Button } from '../Common/button';
import {
  NAV_SECTION_IDS,
  isNavItemHiddenByExperience,
  moveItem,
  navItemResource,
} from '../../lib/profileUi';
import {
  LANDING_PAGES,
  type ControlledResource,
  type LandingPage,
  type NavItemId,
  type NavSection,
  type ProfileExperience,
} from '../../types/profiles';

/** Sidebar labels reused for the catalog items; project items reuse the landing page labels. */
const NAV_ITEM_LABEL_KEYS: Record<NavItemId, string> = {
  welcome: 'sidebar.welcome',
  dashboard: 'sidebar.dashboard',
  domains: 'sidebar.domains.root',
  usecases: 'sidebar.useCases.root',
  projects: 'profiles.form.landingPageOptions.projects',
  gallery: 'profiles.form.landingPageOptions.gallery',
  portfolio: 'profiles.form.landingPageOptions.portfolio',
  cadrages: 'sidebar.cadrages.root',
  cowork: 'sidebar.cowork',
  applications: 'sidebar.forge.applications',
  forges: 'sidebar.forge.forges',
  conversations: 'sidebar.knowledge.conversations',
  chatbots: 'sidebar.knowledge.chatbots',
  'knowledge-base': 'sidebar.knowledge.knowledgeBase',
  evaluations: 'sidebar.evaluations',
};

const NAV_SECTION_LABEL_KEYS: Record<NavSection, string> = {
  main: 'profiles.navigation.sections.main',
  business: 'sidebar.sections.business',
  projects: 'profiles.navigation.sections.projects',
  deployment: 'sidebar.sections.deployment',
  knowledge: 'sidebar.sections.knowledge',
};

export type ExperienceChoice = ProfileExperience | '';

/** What an empty choice inherits: the org's own settings (org-scoped profiles only). */
export interface InheritedNavigation {
  /** null: the org has no landing page, the first accessible page is used. */
  landing: LandingPage | null;
  experience: ProfileExperience;
}

interface ProfileNavigationEditorProps {
  landingPage: LandingPage | '';
  onLandingPageChange: (value: LandingPage | '') => void;
  /** '' inherits the organization's experience. */
  experience: ExperienceChoice;
  onExperienceChange: (value: ExperienceChoice) => void;
  hidden: NavItemId[];
  onHiddenChange: (next: NavItemId[]) => void;
  /** Full item order per section (every catalog item listed). */
  order: Record<NavSection, NavItemId[]>;
  onOrderChange: (next: Record<NavSection, NavItemId[]>) => void;
  /** Read grant of the permissions being edited. */
  canRead: (resource: ControlledResource) => boolean;
  /** Org values shown next to "Inherit"; null for global profiles, whose members span orgs. */
  inherited?: InheritedNavigation | null;
  readOnly?: boolean;
}

/** Navigation tab of the profile editor (plan §9.2, §9.4). */
export const ProfileNavigationEditor: React.FC<ProfileNavigationEditorProps> = ({
  landingPage,
  onLandingPageChange,
  experience,
  onExperienceChange,
  hidden,
  onHiddenChange,
  order,
  onOrderChange,
  canRead,
  inherited = null,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const effectiveExperience: ExperienceChoice = experience || inherited?.experience || '';

  const landingInheritLabel = inherited
    ? t('profiles.form.landingPageInheritValue', 'Inherit from the organization ({{page}})', {
        page: inherited.landing
          ? t(`profiles.form.landingPageOptions.${inherited.landing}`)
          : t('profiles.form.landingPageAuto', 'automatic'),
      })
    : t('profiles.form.landingPageInherit', 'Inherit from the organization');
  const experienceInheritLabel = inherited
    ? t('profiles.navigation.experienceInheritValue', 'Inherit from the organization ({{experience}})', {
        experience:
          inherited.experience === 'olise'
            ? t('profiles.navigation.experienceOlise', 'Olisé')
            : t('profiles.navigation.experienceEtabli', 'Établi'),
      })
    : t('profiles.navigation.experienceInherit', 'Inherit from the organization');

  const toggleHidden = (item: NavItemId, hide: boolean) => {
    onHiddenChange(hide ? [...hidden.filter((h) => h !== item), item] : hidden.filter((h) => h !== item));
  };

  const move = (section: NavSection, index: number, delta: -1 | 1) => {
    onOrderChange({ ...order, [section]: moveItem(order[section], index, delta) });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1 block" htmlFor="profile-landing-page">
            {t('profiles.form.landingPage', 'Default welcome page')}
          </label>
          <select
            id="profile-landing-page"
            className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
            value={landingPage}
            disabled={readOnly}
            onChange={(e) => onLandingPageChange(e.target.value as LandingPage | '')}
          >
            <option value="">{landingInheritLabel}</option>
            {LANDING_PAGES.map((page) => (
              <option key={page} value={page}>
                {t(`profiles.form.landingPageOptions.${page}`)}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {t(
              'profiles.form.landingPageHelp',
              "Replaces the organization's landing page for members with this profile. Falls back to an accessible page when this one is not permitted."
            )}
          </p>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" htmlFor="profile-experience">
            {t('profiles.navigation.experience', 'Experience')}
          </label>
          <select
            id="profile-experience"
            className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
            value={experience}
            disabled={readOnly}
            onChange={(e) => onExperienceChange(e.target.value as ExperienceChoice)}
          >
            <option value="">{experienceInheritLabel}</option>
            <option value="etabli">{t('profiles.navigation.experienceEtabli', 'Établi')}</option>
            <option value="olise">{t('profiles.navigation.experienceOlise', 'Olisé')}</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {t(
              'profiles.navigation.experienceHelp',
              'The Olisé experience hides use cases from the menu and the dashboard.'
            )}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 text-sm rounded-md border border-border bg-muted/30 p-3">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="text-muted-foreground">
          {t(
            'profiles.navigation.hideIsNotForbid',
            'Hiding an item only removes it from the menu; the page stays reachable. To block access, remove the read permission in the Permissions tab.'
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {NAV_SECTION_IDS.map((section) => (
          <div key={section} className="border border-border rounded-lg">
            <div className="px-3 py-2 bg-muted/40 rounded-t-lg">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t(NAV_SECTION_LABEL_KEYS[section])}
              </h3>
              {section === 'projects' && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('profiles.navigation.projectsModuleNote', 'Shown once the Projects module is enabled for the organization.')}
                </p>
              )}
            </div>
            <ul className="divide-y divide-border/60">
              {order[section].map((item, index) => {
                const resource = navItemResource(section, item);
                const readable = resource ? canRead(resource) : false;
                const experienceHidden =
                  effectiveExperience !== '' && isNavItemHiddenByExperience(item, effectiveExperience);
                const isHidden = hidden.includes(item);
                const label = t(NAV_ITEM_LABEL_KEYS[item]);
                const checkboxId = `nav-hide-${item}`;
                return (
                  <li
                    key={item}
                    className="flex items-center gap-2 px-3 py-2 text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className={readable ? 'font-medium' : 'font-medium text-muted-foreground'}>
                        {label}
                      </div>
                      {!readable && resource && (
                        <div className="text-xs text-muted-foreground">
                          {t('profiles.navigation.noRead', 'Requires read permission on {{resource}}', { resource })}
                        </div>
                      )}
                      {readable && experienceHidden && (
                        <div className="text-xs text-muted-foreground">
                          {t('profiles.navigation.hiddenByExperience', 'Hidden by the Olisé experience')}
                        </div>
                      )}
                    </div>
                    <label
                      htmlFor={checkboxId}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <input
                        id={checkboxId}
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                        checked={isHidden}
                        disabled={readOnly || !readable}
                        onChange={(e) => toggleHidden(item, e.target.checked)}
                      />
                      {t('profiles.navigation.hide', 'Hide')}
                    </label>
                    <div className="flex">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        disabled={readOnly || index === 0}
                        onClick={() => move(section, index, -1)}
                        aria-label={t('profiles.navigation.moveUp', 'Move up {{item}}', { item: label })}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        disabled={readOnly || index === order[section].length - 1}
                        onClick={() => move(section, index, 1)}
                        aria-label={t('profiles.navigation.moveDown', 'Move down {{item}}', { item: label })}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};
